var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDareDropApi } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
// Redesign follow/unfollow for the creator profile header. Wires the legacy AUDIT_FOLLOW_CREATOR
// gateway endpoint (the same toggle the legacy heart used via `auditFollowCreator`) through the v2
// fetcher instead of the Redux thunk. Optimistic local toggle (zero layout shift), reconciled by
// the caller's profile refetch. Unauthenticated users get a redesign toast prompting sign-in
// (the legacy FOLLOW_CREATOR_MODAL is intentionally not ported).
export const useCreatorFollow = ({ recordId, following, onChanged }) => {
    const { fetcher } = useDareDropApi();
    const isAuthenticated = useIsAuthenticated();
    const [optimisticFollowing, setOptimisticFollowing] = useState(undefined);
    const isFollowing = optimisticFollowing !== null && optimisticFollowing !== void 0 ? optimisticFollowing : following;
    const mutation = useMutation({
        mutationKey: ['redesign-follow-creator', recordId],
        mutationFn: () => __awaiter(void 0, void 0, void 0, function* () {
            if (!recordId) {
                return;
            }
            // AUDIT_FOLLOW_CREATOR is an untyped JS gateway action, so its inferred payload is `undefined`;
            // cast the `{ recordId }` body (the legacy `auditFollowCreator` payload) to satisfy the generic.
            yield fetcher({
                endpointId: 'AUDIT_FOLLOW_CREATOR',
                payload: { recordId },
            }, true);
        }),
        onMutate: () => {
            const next = !isFollowing;
            setOptimisticFollowing(next);
            return next;
        },
        onSuccess: (_data, _variables, next) => {
            toast.success(next ? 'You are now following this creator.' : 'You have unfollowed this creator.');
            onChanged();
        },
        onError: () => {
            setOptimisticFollowing(undefined);
            toast.error('Could not update follow. Please try again.');
        },
    });
    const toggleFollow = () => {
        if (!isAuthenticated) {
            toast('Sign in to follow this creator.');
            return;
        }
        if (mutation.isPending) {
            return;
        }
        mutation.mutate();
    };
    return {
        isFollowing,
        isPending: mutation.isPending,
        toggleFollow,
    };
};
